import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Trash2,
  Pencil,
  FileDown,
  Loader2,
  ReceiptText,
  CircleDollarSign,
  CheckCircle2,
  Clock3,
  XCircle,
  Send,
  FilePlus2,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_ITEM = {
  description: "",
  quantity: 1,
  unit_price: 0,
  product_id: null,
};

const EMPTY_FORM = {
  client_id: "",
  title: "",
  description: "",
  valid_until: "",
  status: "borrador",
  tax_rate: 21,
  notes: "",
  items: [{ ...EMPTY_ITEM }],
};

const STATUS_OPTIONS = [
  { value: "borrador", label: "Borrador" },
  { value: "enviado", label: "Enviado" },
  { value: "aceptado", label: "Aceptado" },
  { value: "rechazado", label: "Rechazado" },
];

const STATUS_META = {
  borrador: {
    label: "Borrador",
    color: "#86868B",
    bg: "#F5F5F7",
  },
  enviado: {
    label: "Enviado",
    color: "#0066CC",
    bg: "#EAF2FC",
  },
  aceptado: {
    label: "Aceptado",
    color: "#248A3D",
    bg: "#E9F9EE",
  },
  rechazado: {
    label: "Rechazado",
    color: "#D70015",
    bg: "#FFEBEA",
  },
};

function eur(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: color + "15" }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>

      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [inventoryProductId, setInventoryProductId] = useState("");
  const [inventoryQuantity, setInventoryQuantity] = useState(1);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [changingId, setChangingId] = useState(null);

  const load = async () => {
    setLoading(true);

    try {
      const [quotesResponse, clientsResponse, inventoryResponse] = await Promise.all([
        api.get("/quotes"),
        api.get("/clients"),
        api.get("/inventory/products"),
      ]);

      setQuotes(quotesResponse.data || []);
      setClients(clientsResponse.data || []);
      setInventoryProducts((inventoryResponse.data || []).filter((product) => product.active !== false));
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      items: [{ ...EMPTY_ITEM }],
    });
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (quote) => {
    setEditing(quote);

    setForm({
      client_id: quote.client_id || "",
      title: quote.title || "",
      description: quote.description || "",
      valid_until: quote.valid_until
        ? String(quote.valid_until).slice(0, 10)
        : "",
      status: quote.status || "borrador",
      tax_rate: quote.tax_rate ?? 21,
      notes: quote.notes || "",
      items:
        quote.items?.length > 0
          ? quote.items.map((item) => ({
              description: item.description || "",
              quantity: item.quantity ?? 1,
              unit_price: item.unit_price ?? 0,
              product_id: item.product_id || null,
            }))
          : [{ ...EMPTY_ITEM }],
    });

    setOpen(true);
  };

  const updateItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [key]: value,
            }
          : item
      ),
    }));
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [...current.items, { ...EMPTY_ITEM }],
    }));
  };

  const addInventoryProduct = () => {
    const product = inventoryProducts.find(
      (item) => item.id === inventoryProductId
    );

    const quantity = Number(inventoryQuantity) || 0;

    if (!product) {
      toast.error("Selecciona un material del inventario.");
      return;
    }

    if (quantity <= 0) {
      toast.error("Introduce una cantidad válida.");
      return;
    }

    setForm((current) => {
      const firstIsEmpty =
        current.items.length === 1 &&
        !current.items[0].description.trim();

      const newItem = {
        description: `${product.name}${product.sku ? ` (${product.sku})` : ""}`,
        quantity,
        unit_price: Number(product.sale_price || 0),
        product_id: product.id,
      };

      return {
        ...current,
        items: firstIsEmpty
          ? [newItem]
          : [...current.items, newItem],
      };
    });

    setInventoryProductId("");
    setInventoryQuantity(1);
    toast.success("Material añadido al presupuesto.");
  };

  const removeItem = (index) => {
    setForm((current) => {
      if (current.items.length === 1) {
        return current;
      }

      return {
        ...current,
        items: current.items.filter(
          (_, itemIndex) => itemIndex !== index
        ),
      };
    });
  };

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      return sum + quantity * unitPrice;
    }, 0);

    const tax = subtotal * ((Number(form.tax_rate) || 0) / 100);

    return {
      subtotal,
      tax,
      total: subtotal + tax,
    };
  }, [form.items, form.tax_rate]);

  const save = async (event) => {
    event.preventDefault();

    if (!form.client_id) {
      toast.error("Selecciona un cliente.");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Escribe el título del presupuesto.");
      return;
    }

    const cleanItems = form.items
      .map((item) => ({
        description: item.description.trim(),
        quantity: Number(item.quantity) || 0,
        unit_price: Number(item.unit_price) || 0,
        product_id: item.product_id || null,
      }))
      .filter(
        (item) =>
          item.description &&
          item.quantity > 0
      );

    if (!cleanItems.length) {
      toast.error("Añade al menos una partida válida.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        notes: form.notes.trim(),
        valid_until: form.valid_until || null,
        tax_rate: Number(form.tax_rate) || 0,
        items: cleanItems,
      };

      if (editing) {
        await api.put("/quotes/" + editing.id, payload);
        toast.success("Presupuesto actualizado.");
      } else {
        await api.post("/quotes", payload);
        toast.success("Presupuesto creado.");
      }

      setOpen(false);
      resetForm();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (quote, status) => {
    setChangingId(quote.id);

    try {
      await api.put("/quotes/" + quote.id, {
        status,
      });

      setQuotes((current) =>
        current.map((item) =>
          item.id === quote.id
            ? { ...item, status }
            : item
        )
      );

      toast.success("Estado actualizado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setChangingId(null);
    }
  };

  const remove = async (quote) => {
    const confirmed = window.confirm(
      '¿Seguro que quieres eliminar el presupuesto "' +
        quote.number +
        '"?'
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(quote.id);

    try {
      await api.delete("/quotes/" + quote.id);

      setQuotes((current) =>
        current.filter((item) => item.id !== quote.id)
      );

      toast.success("Presupuesto eliminado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const openPdf = (quote) => {
    const token = localStorage.getItem("mantix_token");

    window.open(
      api.defaults.baseURL +
        "/quotes/" +
        quote.id +
        "/pdf?auth=" +
        encodeURIComponent(token || ""),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const createInvoiceFromQuote = (quote) => {
    if (quote.status !== "aceptado") {
      toast.error("Primero marca el presupuesto como aceptado.");
      return;
    }

    navigate("/facturas?quote=" + encodeURIComponent(quote.id));
  };

  const filteredQuotes = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return quotes.filter((quote) => {
      const matchesQuery =
        !normalized ||
        [
          quote.number,
          quote.title,
          quote.client_name,
          quote.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesStatus =
        statusFilter === "todos" ||
        quote.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [quotes, query, statusFilter]);

  const accepted = quotes.filter(
    (quote) => quote.status === "aceptado"
  );

  const pending = quotes.filter((quote) =>
    ["borrador", "enviado"].includes(quote.status)
  );

  const acceptedTotal = accepted.reduce(
    (sum, quote) => sum + Number(quote.total || 0),
    0
  );

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Presupuestos"
        subtitle={
          quotes.length +
          " presupuestos · " +
          eur(
            quotes.reduce(
              (sum, quote) =>
                sum + Number(quote.total || 0),
              0
            )
          ) +
          " en total"
        }
        action={
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);

              if (!nextOpen) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="new-quote-btn"
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo presupuesto
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                  {editing
                    ? "Editar presupuesto"
                    : "Nuevo presupuesto"}
                </DialogTitle>

                <p className="mt-1 text-sm text-[#86868B]">
                  Añade las partidas, el IVA y la fecha de validez.
                </p>
              </DialogHeader>

              <form onSubmit={save} className="mt-4 space-y-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Cliente
                    </label>

                    <select
                      value={form.client_id}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          client_id: event.target.value,
                        })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      <option value="">
                        Selecciona cliente...
                      </option>

                      {clients.map((client) => (
                        <option
                          key={client.id}
                          value={client.id}
                        >
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Estado
                    </label>

                    <select
                      value={form.status}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          status: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option
                          key={status.value}
                          value={status.value}
                        >
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Título
                  </label>

                  <input
                    value={form.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        title: event.target.value,
                      })
                    }
                    required
                    placeholder="Ej. Sustitución del sistema de climatización"
                    className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Descripción
                  </label>

                  <textarea
                    value={form.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        description: event.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Descripción general del trabajo..."
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
                  />
                </div>

                <div className="rounded-2xl border border-[#D2D2D7]/60 bg-white p-4">
                  <div className="mb-3">
                    <h3 className="font-semibold text-[#1D1D1F]">
                      Materiales del inventario
                    </h3>
                    <p className="text-sm text-[#86868B]">
                      Añade productos reales al presupuesto. No se descuenta stock hasta que se utilicen en una orden de trabajo.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_120px_auto]">
                    <select
                      value={inventoryProductId}
                      onChange={(event) => setInventoryProductId(event.target.value)}
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="">Selecciona material...</option>
                      {inventoryProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.sku ? `${product.sku} · ` : ""}
                          {product.name} · Stock {product.stock} {product.unit || "ud"} · Venta {eur(product.sale_price)}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={inventoryQuantity}
                      onChange={(event) => setInventoryQuantity(event.target.value)}
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                      placeholder="Cantidad"
                    />

                    <button
                      type="button"
                      onClick={addInventoryProduct}
                      className="rounded-xl bg-[#0066CC] px-5 py-3 text-sm font-medium text-white"
                    >
                      Añadir material
                    </button>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1D1D1F]">
                        Partidas
                      </h3>
                      <p className="text-sm text-[#86868B]">
                        Detalla materiales, servicios y mano de obra.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center gap-2 rounded-full bg-[#0066CC]/10 px-4 py-2 text-sm font-medium text-[#0066CC]"
                    >
                      <Plus className="h-4 w-4" />
                      Añadir partida
                    </button>
                  </div>

                  <div className="space-y-3">
                    {form.items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 gap-3 rounded-2xl bg-[#F5F5F7] p-4 md:grid-cols-[minmax(0,1fr)_120px_150px_44px]"
                      >
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Descripción
                          </label>

                          <input
                            value={item.description}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "description",
                                event.target.value
                              )
                            }
                            placeholder="Ej. Bomba de circulación"
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Cantidad
                          </label>

                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "quantity",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-medium text-[#86868B]">
                            Precio unidad
                          </label>

                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(event) =>
                              updateItem(
                                index,
                                "unit_price",
                                event.target.value
                              )
                            }
                            className="w-full rounded-xl bg-white px-3 py-2.5 outline-none"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            disabled={form.items.length === 1}
                            className="flex h-10 w-10 items-center justify-center rounded-xl text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-30"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      IVA (%)
                    </label>

                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={form.tax_rate}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          tax_rate: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Válido hasta
                    </label>

                    <input
                      type="date"
                      value={form.valid_until}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          valid_until: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="rounded-2xl bg-[#EAF2FC] p-4">
                    <p className="text-xs font-medium text-[#86868B]">
                      Total del presupuesto
                    </p>
                    <p className="mt-1 font-display text-2xl font-bold text-[#0066CC]">
                      {eur(totals.total)}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Notas y condiciones
                  </label>

                  <textarea
                    value={form.notes}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        notes: event.target.value,
                      })
                    }
                    rows={3}
                    placeholder="Condiciones, garantía, forma de pago..."
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="rounded-2xl border border-[#D2D2D7]/60 p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">
                      Base imponible
                    </span>
                    <span>{eur(totals.subtotal)}</span>
                  </div>

                  <div className="mt-2 flex justify-between text-sm">
                    <span className="text-[#86868B]">
                      IVA ({Number(form.tax_rate) || 0}%)
                    </span>
                    <span>{eur(totals.tax)}</span>
                  </div>

                  <div className="mt-3 flex justify-between border-t border-[#E8E8ED] pt-3 font-semibold">
                    <span>Total</span>
                    <span className="text-[#0066CC]">
                      {eur(totals.total)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium hover:bg-[#F5F5F7]"
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white hover:bg-[#004499] disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}

                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Crear presupuesto"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={ReceiptText}
          label="Presupuestos"
          value={quotes.length}
          color="#0066CC"
        />

        <SummaryCard
          icon={Clock3}
          label="Pendientes"
          value={pending.length}
          color="#FF9500"
        />

        <SummaryCard
          icon={CheckCircle2}
          label="Aceptados"
          value={accepted.length}
          color="#34C759"
        />

        <SummaryCard
          icon={CircleDollarSign}
          label="Aceptado"
          value={eur(acceptedTotal)}
          color="#5856D6"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por número, título o cliente..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todos los estados</option>

            {STATUS_OPTIONS.map((status) => (
              <option
                key={status.value}
                value={status.value}
              >
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <p className="mt-3 text-xs text-[#86868B]">
          Mostrando {filteredQuotes.length} de {quotes.length} presupuestos
        </p>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="mantix-card py-8">
          <EmptyState
            title="No hay presupuestos"
            subtitle="Crea tu primer presupuesto para enviarlo a un cliente."
          />

          <div className="-mt-4 flex justify-center pb-6">
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white"
            >
              <Plus className="h-4 w-4" />
              Crear presupuesto
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[#D2D2D7]/50 bg-white">
          <div className="hidden grid-cols-[150px_minmax(220px,1fr)_200px_160px_170px_190px] gap-4 border-b border-[#E8E8ED] px-6 py-4 text-xs font-semibold uppercase tracking-wide text-[#86868B] lg:grid">
            <span>Número</span>
            <span>Presupuesto</span>
            <span>Cliente</span>
            <span>Total</span>
            <span>Estado</span>
            <span className="text-right">Acciones</span>
          </div>

          {filteredQuotes.map((quote) => {
            const meta =
              STATUS_META[quote.status] ||
              STATUS_META.borrador;

            const isDeleting = deletingId === quote.id;
            const isChanging = changingId === quote.id;

            return (
              <article
                key={quote.id}
                className="grid grid-cols-1 gap-4 border-b border-[#E8E8ED] px-6 py-5 last:border-b-0 lg:grid-cols-[150px_minmax(220px,1fr)_200px_160px_170px_190px] lg:items-center"
              >
                <div>
                  <p className="font-mono text-sm font-semibold text-[#1D1D1F]">
                    {quote.number}
                  </p>
                  <p className="mt-1 text-xs text-[#86868B]">
                    {quote.created_at
                      ? String(quote.created_at).slice(0, 10)
                      : "—"}
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-[#1D1D1F]">
                    {quote.title}
                  </h3>
                  {quote.valid_until && (
                    <p className="mt-1 text-xs text-[#86868B]">
                      Válido hasta:{" "}
                      {String(quote.valid_until).slice(0, 10)}
                    </p>
                  )}
                </div>

                <p className="text-sm text-[#86868B]">
                  {quote.client_name || "—"}
                </p>

                <p className="font-mono font-semibold text-[#1D1D1F]">
                  {eur(quote.total)}
                </p>

                <select
                  value={quote.status}
                  disabled={isChanging}
                  onChange={(event) =>
                    changeStatus(quote, event.target.value)
                  }
                  className="rounded-full px-3 py-2 text-sm font-medium outline-none"
                  style={{
                    color: meta.color,
                    backgroundColor: meta.bg,
                  }}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </option>
                  ))}
                </select>

                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(quote)}
                    title="Editar"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => openPdf(quote)}
                    title="Abrir PDF"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <FileDown className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      changeStatus(quote, "enviado")
                    }
                    title="Marcar como enviado"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                  >
                    <Send className="h-4 w-4" />
                  </button>

                  {quote.status === "aceptado" && (
                    <button
                      type="button"
                      onClick={() => createInvoiceFromQuote(quote)}
                      title="Crear factura desde este presupuesto"
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#E9F9EE] hover:text-[#248A3D]"
                    >
                      <FilePlus2 className="h-4 w-4" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => remove(quote)}
                    disabled={isDeleting}
                    title="Eliminar"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}